import React, { Fragment } from "react";
import { getGenericLink, getHreflangs, getLanguages, getLinkWithSlug, hostOfUri } from "core-web/utils/multimarket";

import Row from "theme/layouts/Row";
import TextLink from "theme/components/text/TextLink";
import TextStyles from "theme/components/text/TextStyles";
import { inBrowser } from "core-web/constants";
import { useSelector } from "react-redux";
import styled from "theme/libs/styled";
import useAboveBreakpoint from "core-web/hooks/useAboveBreakpoint";

const CurrentLanguage = styled(TextStyles)`
    position: relative;

    ::after {
        display: block;
        content: "";
        margin-top: 2px;
        border-bottom: solid 1px currentColor;
        position: absolute;
        left: 0;
        right: 0;
        bottom: -3px;
    }
`;

const SiteSelector = ({ ...rest }) => {
    const isAboveTabletLg = useAboveBreakpoint("tabletLg");
    const currentLocale = useSelector(({ application }) => application.locale) || "";
    const availableLanguages = useSelector(({ application }) => application.languages) || {};
    const localeAlternates = useSelector(({ page }) => page.localeAlternates) || {};
    const origin = useSelector(({ application }) => application.origin) || "";
    const ecommerceEnabled = useSelector(({ application }) => application?.ecommerceEnabled);

    // A /sv <-> /en toggle for the base site; the shops (.com, .co.uk) do not get one.
    // The return has to stay below every hook — ecommerceEnabled flips false -> true during
    // hydration, so an early return above them would change the hook count between renders.
    if (ecommerceEnabled) {
        return null;
    }

    // This is a language switcher: it offers the languages of the site being viewed (/sv and /en on
    // www.wallmek.se). Other markets live on their own domains (wallmektools.com, wallmek.co.uk)
    // and are excluded by host.
    const currentHost = (origin || (inBrowser ? window.location.host : "")).toLowerCase();
    const filteredLanguages = availableLanguages.filter(({ uri }) => hostOfUri(uri) === currentHost);
    const languages = getLanguages(filteredLanguages, currentLocale);

    // A single market is not a choice — render nothing rather than a lone, non-clickable label.
    if (languages.length < 2) {
        return null;
    }

    let hrefLangs;

    if (localeAlternates) {
        hrefLangs = getHreflangs(localeAlternates, languages);
    }

    const countryLink = (locale) => {
        let link = getGenericLink(locale, languages);
        if (!!hrefLangs?.length) {
            link = getLinkWithSlug(locale, hrefLangs, languages);
        }

        if (inBrowser) {
            const query = window.location.search || "";
            if (query.charAt(0) === "?") {
                link += query;
            }
        }

        return link;
    };

    const languageName = {
        sv_SE: "SWE",
        en_SE: "ENG",
        en_GB: "ENG",
    };

    return (
        <Row gap="4px" {...rest}>
            {languages.map((lang, i) => {
                // getGenericLink/getHreflangs already return the target market's full URL,
                // including its own /sv|/en path — stripping it again lands on the wrong app.
                const link = countryLink(lang?.locale);
                const isCurrent = lang?.locale === currentLocale;

                return (
                    <Fragment key={`${lang?.locale}`}>
                        {isCurrent ? (
                            <CurrentLanguage textStyles={isAboveTabletLg ? "Myriad Pro/12-100" : "Myriad Pro/18-100"}>
                                {languageName[lang?.locale] || lang?.name}
                            </CurrentLanguage>
                        ) : (
                            <TextLink
                                to={link}
                                data-current={isCurrent}
                                revertAnimation={isCurrent}
                                target="_self"
                                textStyles={isAboveTabletLg ? "Myriad Pro/12-100" : "Myriad Pro/18-100"}
                            >
                                {languageName[lang?.locale] || lang?.name}
                            </TextLink>
                        )}

                        {i !== languages.length - 1 && (
                            <TextStyles
                                as="span"
                                textStyles={isAboveTabletLg ? "Myriad Pro/12-100" : "Myriad Pro/18-100"}
                            >
                                |
                            </TextStyles>
                        )}
                    </Fragment>
                );
            })}
        </Row>
    );
};

export default SiteSelector;
